/**
 * Email broadcast — write one message and send it to one customer, several, or a whole group.
 *
 * The sibling of the push broadcast, and deliberately harder to fire. A push is a line of text on
 * a lock screen that the delivery service withholds from anyone who muted its category. An email
 * is a document that lands in an inbox, is forwarded, is screenshotted, and is kept. There is no
 * recall once the mail server has accepted it.
 *
 * ## Three steps, not one
 *
 *   1. **Compose.** A body, like a template: the active layout supplies the chrome, so a
 *      broadcast looks like everything else the platform sends without anybody rebuilding it.
 *   2. **Check.** The API resolves the audience and answers with two numbers, and the second is
 *      the one that matters: `matched` is who the selector picked, `deliverable` is who will
 *      actually be mailed. The gap is everyone with no address and everyone who opted out of
 *      marketing, and quoting the first as "recipients" is a promise the platform will not keep.
 *   3. **Send.** A draft is created, then sent as a separate act. Composing and sending are two
 *      decisions and only the second one is irreversible, so they are two requests.
 *
 * The last gesture is typed rather than clicked. See `TypedConfirmModal` for why.
 *
 * ## Always non-transactional
 *
 * Every broadcast honours the customer's marketing preference, structurally: this path cannot
 * send a transactional message. A receipt must reach somebody who unsubscribed from newsletters,
 * and a newsletter must not, and the only safe way to guarantee that is to keep them apart.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { useBrand } from '../branding/BrandProvider.js';
import { adminCan } from '../contracts/permissions.js';
import type {
  AdminUserRow,
  BroadcastCategory,
  BroadcastPreviewResponse,
  EmailBroadcast,
  EmailImage,
  EmailLayout,
  EmailTemplate,
  KycTier,
  PlatformSettings,
} from '../contracts/types.js';
import { BROADCAST_AUDIENCE_KINDS, BROADCAST_CATEGORIES, KYC_TIERS } from '../contracts/vocab.js';
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  LoadingState,
  SearchInput,
  Select,
} from '../components/ui.js';
import { EmailImageLibrary } from '../components/EmailImageLibrary.js';
import { EmailPreview } from '../components/EmailPreview.js';
import { TypedConfirmModal } from '../components/TypedConfirmModal.js';
import { IconSend, IconX } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { relativeTime } from '../lib/format.js';
import {
  AUDIENCE_KIND_INFO,
  BROADCAST_MAX_NAMED_USERS,
  buildAudience,
  describePreview,
  emptyAudienceDraft,
  recipientLabel,
  splitAddressable,
  type AudienceDraft,
} from '../lib/emailAudience.js';
import { referencedImages } from '../lib/emailAttachments.js';
import { defaultEmailLayoutContent, emailTokens, renderWithLayout } from '../lib/emailLayout.js';
import { renderPreview } from '../lib/emailVars.js';

/**
 * What the operator has to type to send.
 *
 * Console policy rather than contract: the API has no opinion on how deliberate the last click
 * is. Deliberately short and upper case, long enough to be typed on purpose and short enough
 * that nobody reaches for the clipboard.
 */
const CONFIRM_PHRASE = 'SEND';

const SUBJECT_MAX = 255;
const PREHEADER_MAX = 200;
const BODY_MAX = 200_000;

const BLANK_BODY = `<h1 style="margin:0 0 16px 0;font-size:22px;line-height:30px;font-weight:700;color:{{layoutHeading}};">Your headline</h1>
<p style="margin:0 0 16px 0;font-size:15px;line-height:24px;color:{{layoutBody}};">Write the message here. The frame around it comes from your email layout, so this will look like everything else you send.</p>`;

interface Loaded {
  readonly settings: PlatformSettings;
  readonly layout: EmailLayout | null;
  readonly templates: readonly EmailTemplate[];
  readonly images: readonly EmailImage[];
  readonly broadcasts: readonly EmailBroadcast[];
}

export function EmailBroadcastPage(): JSX.Element {
  const { api } = useAuth();

  const load = useCallback(async (): Promise<Loaded> => {
    const [settings, layout, templates, images, broadcasts] = await Promise.all([
      api.getBusinessSettings(),
      api.getEmailLayout(),
      api.listEmailTemplates(),
      api.listEmailImages(),
      api.listEmailBroadcasts(),
    ]);
    return {
      settings,
      layout,
      templates: templates.templates,
      images: images.images,
      broadcasts: broadcasts.broadcasts,
    };
  }, [api]);

  const state = useAsync(load, [load]);

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Email broadcast</h1>
        <p className="page-head__sub">
          One message, sent to a customer, a handful of them, or a whole group. It wears your
          email layout, so it looks like everything else the platform sends. A broadcast cannot be
          recalled.
        </p>
      </div>

      {state.loading ? (
        <LoadingState label="Loading the composer…" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : state.data === null ? (
        <ErrorBox message="The composer is not available." />
      ) : (
        <Composer
          data={state.data}
          onImages={(images) => state.setData((prev) => (prev === null ? prev : { ...prev, images }))}
          onSent={(broadcast) =>
            state.setData((prev) =>
              prev === null ? prev : { ...prev, broadcasts: [broadcast, ...prev.broadcasts] },
            )
          }
        />
      )}
    </>
  );
}

function Composer({
  data,
  onImages,
  onSent,
}: {
  data: Loaded;
  onImages: (next: readonly EmailImage[]) => void;
  onSent: (next: EmailBroadcast) => void;
}): JSX.Element {
  const { api, session } = useAuth();
  const brand = useBrand();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'admins:write');
  const layout = data.layout?.content ?? defaultEmailLayoutContent();

  const [audience, setAudience] = useState<AudienceDraft>(() => emptyAudienceDraft());
  const [selected, setSelected] = useState<readonly AdminUserRow[]>([]);
  const [category, setCategory] = useState<BroadcastCategory>('marketing');

  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [bodyHtml, setBodyHtml] = useState(BLANK_BODY);

  const [preview, setPreview] = useState<BroadcastPreviewResponse | null>(null);
  const [checking, setChecking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState<EmailBroadcast | null>(null);
  // A send is irreversible, so the guard against a second one is a ref rather than state: state
  // updates are batched and a fast double-activation can read a stale `false`.
  const inFlight = useRef(false);

  const draft: AudienceDraft = useMemo(
    () => ({ ...audience, userIds: selected.map((u) => u.id) }),
    [audience, selected],
  );
  const built = buildAudience(draft);

  // Any change to the selector invalidates a count that was taken against the old one. Left
  // standing, the confirm dialog would quote a number for a population nobody chose.
  useEffect(() => {
    setPreview(null);
  }, [draft, category]);

  const tokens = useMemo(
    () => emailTokens(data.settings, layout, brand.logoUrl),
    [data.settings, layout, brand.logoUrl],
  );

  const renderedBody = useMemo(() => renderPreview(bodyHtml, tokens), [bodyHtml, tokens]);
  const renderedSubject = useMemo(() => renderPreview(subject, tokens), [subject, tokens]);
  const document = useMemo(
    () =>
      renderWithLayout(
        layout,
        {
          platformName: data.settings.platformName,
          logoUrl: brand.logoUrl,
          supportEmail: data.settings.supportEmail,
          websiteUrl: data.settings.websiteUrl,
        },
        { preheader, bodyHtml: renderedBody },
      ),
    [layout, data.settings, brand.logoUrl, preheader, renderedBody],
  );

  const used = useMemo(() => referencedImages(bodyHtml, data.images), [bodyHtml, data.images]);

  const described = describePreview(preview);
  const tooLong = bodyHtml.length > BODY_MAX;
  const composed = subject.trim() !== '' && bodyHtml.trim() !== '';
  const audienceError = 'error' in built ? built.error : null;
  const canCheck = canWrite && composed && !tooLong && audienceError === null;
  const canSend = canCheck && preview !== null && preview.deliverable > 0;

  async function check(): Promise<void> {
    if (!canCheck || checking || 'error' in built) return;
    setChecking(true);
    try {
      setPreview(await api.previewEmailBroadcast({ audience: built.audience, category }));
    } catch (err) {
      toast.error('Could not check the audience', messageOf(err));
    } finally {
      setChecking(false);
    }
  }

  /**
   * Create the draft, then send it.
   *
   * Two requests because they are two decisions. It also means a failure between them leaves a
   * draft nobody sent rather than an unknown state: the broadcast list shows it, and an operator
   * can look before trying again.
   */
  async function send(): Promise<void> {
    if (inFlight.current || 'error' in built) return;
    inFlight.current = true;
    setSending(true);
    try {
      const created = await api.createEmailBroadcast({
        subject: subject.trim(),
        preheader: preheader.trim(),
        bodyHtml,
        audience: built.audience,
        category,
      });
      const sent = await api.sendEmailBroadcast(created.id);
      setConfirmOpen(false);
      setLastSent(sent);
      onSent(sent);
      toast.success('Broadcast sent', `${sent.totalRecipients.toLocaleString()} recipient(s).`);
      // Cleared only on a confirmed success, so a failure never costs the operator the message.
      setSubject('');
      setPreheader('');
      setBodyHtml(BLANK_BODY);
      setSelected([]);
      setPreview(null);
    } catch (err) {
      // Closed rather than left open to retry. A failure after the server accepted the send
      // looks identical from here to one before it, and a second click would mail everybody
      // twice. The broadcast list is the place to check what actually happened.
      setConfirmOpen(false);
      toast.error(
        'The send did not complete',
        `${messageOf(err)} Check the broadcast list before trying again: a repeat would mail everyone twice.`,
      );
    } finally {
      setSending(false);
      inFlight.current = false;
    }
  }

  return (
    <div className="settings-hub">
      <div className="grid grid--2">
        <div className="settings-hub">
          <Card title="Who this reaches">
            <div className="cat-grid">
              {BROADCAST_AUDIENCE_KINDS.map((kind) => {
                const info = AUDIENCE_KIND_INFO[kind];
                return (
                  <button
                    key={kind}
                    type="button"
                    className={`cat-card${audience.kind === kind ? ' cat-card--on' : ''}`}
                    onClick={() => setAudience((prev) => ({ ...prev, kind }))}
                  >
                    <span className="cat-card__top">
                      <Badge tone="violet" dot>
                        {info.label}
                      </Badge>
                      {audience.kind === kind && <span className="cat-card__check">Selected</span>}
                    </span>
                    <span className="cat-card__hint">{info.hint}</span>
                  </button>
                );
              })}
            </div>

            {audience.kind === 'tier' && (
              <Field label="Which tier" htmlFor="bc-tier">
                <Select
                  id="bc-tier"
                  value={audience.tier}
                  onChange={(e) => setAudience((prev) => ({ ...prev, tier: e.target.value as KycTier }))}
                >
                  {KYC_TIERS.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier.replace('tier_', 'Tier ')}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            {audience.kind === 'signed_up_between' && (
              <div className="grid grid--2" style={{ gap: 14 }}>
                <Field label="From" htmlFor="bc-from">
                  <Input
                    id="bc-from"
                    type="date"
                    value={audience.from}
                    onChange={(e) => setAudience((prev) => ({ ...prev, from: e.target.value }))}
                  />
                </Field>
                <Field label="To" htmlFor="bc-to">
                  <Input
                    id="bc-to"
                    type="date"
                    value={audience.to}
                    onChange={(e) => setAudience((prev) => ({ ...prev, to: e.target.value }))}
                  />
                </Field>
              </div>
            )}

            {audience.kind === 'users' && (
              <CustomerPicker selected={selected} onChange={setSelected} />
            )}

            {audienceError !== null && (
              <div className="form-error" role="alert">
                {audienceError}
              </div>
            )}
          </Card>

          <Card title="Compose">
            <Field label="Start from" htmlFor="bc-template">
              <Select
                id="bc-template"
                disabled={!canWrite}
                defaultValue=""
                onChange={(e) => {
                  const key = e.target.value;
                  if (key === '') {
                    setBodyHtml(BLANK_BODY);
                    return;
                  }
                  const template = data.templates.find((t) => t.key === key);
                  if (template === undefined) return;
                  setBodyHtml(template.html);
                  // Only fill a subject nobody has written into. Overwriting one the operator
                  // typed would lose work to a dropdown they were only browsing.
                  setSubject((prev) => (prev.trim() === '' ? template.subject : prev));
                  setPreheader((prev) => (prev.trim() === '' ? template.preheader : prev));
                }}
              >
                <option value="">A blank message</option>
                {data.templates.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.name}
                  </option>
                ))}
              </Select>
              <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                Copies that template&rsquo;s body as a starting point. Editing here never changes
                the template.
              </div>
            </Field>

            <Field label="Subject" htmlFor="bc-subject">
              <Input
                id="bc-subject"
                value={subject}
                maxLength={SUBJECT_MAX}
                disabled={!canWrite}
                placeholder="What this message is about"
                onChange={(e) => setSubject(e.target.value)}
              />
            </Field>

            <Field label="Preheader" htmlFor="bc-preheader">
              <Input
                id="bc-preheader"
                value={preheader}
                maxLength={PREHEADER_MAX}
                disabled={!canWrite}
                placeholder="The grey line an inbox shows beside the subject"
                onChange={(e) => setPreheader(e.target.value)}
              />
              <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                Leave it empty and inboxes show the first words of the message instead, which is
                rarely what you would have chosen.
              </div>
            </Field>

            <div className="field">
              <label className="field__label" htmlFor="bc-body">
                Message
              </label>
              <textarea
                id="bc-body"
                className="tpl-html"
                spellCheck={false}
                value={bodyHtml}
                disabled={!canWrite}
                onChange={(e) => setBodyHtml(e.target.value)}
              />
              <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                The body only. Your layout draws the header, the card and the footer around it.
                Tokens like <code className="mono">{'{{platformName}}'}</code> and{' '}
                <code className="mono">{'{{layoutHeading}}'}</code> are filled at send time.
              </div>
              {tooLong && (
                <div className="form-error" role="alert" style={{ marginTop: 8 }}>
                  This message is {bodyHtml.length.toLocaleString()} characters. The limit is{' '}
                  {BODY_MAX.toLocaleString()}, so trim it before sending.
                </div>
              )}
            </div>

            <Field label="Kind" htmlFor="bc-category">
              <Select
                id="bc-category"
                value={category}
                disabled={!canWrite}
                onChange={(e) => setCategory(e.target.value as BroadcastCategory)}
              >
                {BROADCAST_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c === 'marketing' ? 'Marketing' : 'Announcement'}
                  </option>
                ))}
              </Select>
              <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                Both honour an unsubscribe. The choice is for your own record and for the wording
                in the footer, and it is not a way to mark a broadcast as transactional.
              </div>
            </Field>
          </Card>

          <EmailImageLibrary
            images={data.images}
            canWrite={canWrite}
            onChange={onImages}
            inUseIds={used.map((i) => i.id)}
            hint="Shared with your templates. Insert one to put its tag in the message."
            onInsert={(_image, tag) => {
              setBodyHtml((prev) => `${prev}\n${tag}`);
              toast.info('Image inserted', 'It went in at the end. Move it if you want it elsewhere.');
            }}
          />
        </div>

        <div className="settings-hub">
          <Card title="Preview" flush>
            <EmailPreview
              subject={renderedSubject}
              preheader={preheader}
              html={document}
              senderName={layout.senderName === '' ? data.settings.platformName : layout.senderName}
              fromEmail={data.settings.supportEmail}
              images={used}
            />
          </Card>

          <Card title="Delivery">
            {preview === null ? (
              <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
                Check the audience to see how many customers this reaches. The number is taken
                fresh each time, because a count from ten minutes ago is a different promise.
              </p>
            ) : (
              <>
                <div className="reach">
                  <span className="reach__num">{(described.deliverable ?? 0).toLocaleString()}</span>
                  <span className="reach__who">
                    {described.deliverable === 1 ? 'person will get this' : 'people will get this'}
                  </span>
                </div>
                {described.detail !== null && (
                  <p className="muted" style={{ fontSize: 13 }}>
                    {described.detail}
                  </p>
                )}
                {preview.deliverable === 0 && (
                  <div className="form-error" role="alert">
                    Nobody in this group can be mailed right now, so there is nothing to send.
                  </div>
                )}
              </>
            )}

            <p className="muted" style={{ fontSize: 13 }}>
              Every recipient gets their own copy. Nobody sees anybody else&rsquo;s address, and
              every broadcast honours an unsubscribe.
            </p>

            <div style={{ display: 'grid', gap: 8 }}>
              <Button
                variant="ghost"
                block
                loading={checking}
                disabled={!canCheck || checking}
                onClick={() => void check()}
              >
                Check the audience
              </Button>
              <Button
                variant="primary"
                block
                disabled={!canSend}
                onClick={() => setConfirmOpen(true)}
              >
                <IconSend size={15} /> Review and send
              </Button>
            </div>

            {lastSent !== null && (
              <div className="deliver-stat">
                <span className="deliver-stat__num">{lastSent.totalRecipients.toLocaleString()}</span>
                <span className="faint">
                  recipients on your last broadcast, {relativeTime(lastSent.createdAt)}
                </span>
              </div>
            )}
          </Card>

          {data.broadcasts.length > 0 && (
            <Card title="Recent broadcasts">
              <ul className="picklist" style={{ maxHeight: 260 }}>
                {data.broadcasts.slice(0, 10).map((b) => (
                  <li key={b.id}>
                    <div className="picklist__row" style={{ cursor: 'default' }}>
                      <span className="picklist__main">
                        <span className="picklist__name">{b.subject}</span>
                        <span className="faint" style={{ fontSize: 12 }}>
                          {b.sentCount.toLocaleString()} sent
                          {b.skippedCount > 0 ? `, ${b.skippedCount.toLocaleString()} skipped` : ''}
                          {b.failedCount > 0 ? `, ${b.failedCount.toLocaleString()} failed` : ''}
                          {' · '}
                          {relativeTime(b.createdAt)}
                        </span>
                      </span>
                      <Badge tone={statusTone(b.status)} dot>
                        {b.status}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>

      <TypedConfirmModal
        open={confirmOpen}
        title="Send this broadcast"
        phrase={CONFIRM_PHRASE}
        headlineCount={preview?.deliverable ?? 0}
        headlineNoun={preview?.deliverable === 1 ? 'person will get this' : 'people will get this'}
        warning="A sent email cannot be recalled, edited or unsent."
        confirmLabel="Send now"
        loading={sending}
        onConfirm={() => void send()}
        onClose={() => setConfirmOpen(false)}
      >
        <dl className="kv">
          <dt>Subject</dt>
          <dd>{renderedSubject.trim() === '' ? 'No subject' : renderedSubject}</dd>
          <dt>Audience</dt>
          <dd>{AUDIENCE_KIND_INFO[audience.kind].label}</dd>
          <dt>Kind</dt>
          <dd>{category === 'marketing' ? 'Marketing' : 'Announcement'}</dd>
          {preview !== null && preview.matched !== preview.deliverable && (
            <>
              <dt>Skipped</dt>
              <dd>{(preview.matched - preview.deliverable).toLocaleString()}</dd>
            </>
          )}
          {used.length > 0 && (
            <>
              <dt>Images</dt>
              <dd>{used.length}</dd>
            </>
          )}
        </dl>
      </TypedConfirmModal>
    </div>
  );
}

function statusTone(status: EmailBroadcast['status']): 'green' | 'red' | 'amber' | 'neutral' {
  if (status === 'completed') return 'green';
  if (status === 'failed' || status === 'cancelled') return 'red';
  if (status === 'sending' || status === 'queued') return 'amber';
  return 'neutral';
}

/**
 * Search the customer list and build a named set.
 *
 * The same search the Users page runs, so an operator finds people the way they already know
 * how. Rows show the name and the email, which is the handle a broadcast actually uses; the
 * phone stays out of it entirely, because nothing on this page takes one.
 */
function CustomerPicker({
  selected,
  onChange,
}: {
  selected: readonly AdminUserRow[];
  onChange: (next: readonly AdminUserRow[]) => void;
}): JSX.Element {
  const { api } = useAuth();
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<readonly AdminUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const id = window.setTimeout(() => {
      api
        .listUsers(query.trim() === '' ? undefined : query.trim())
        .then((res) => {
          if (live) setRows(res.items);
        })
        .catch((err: unknown) => {
          if (live) setError(messageOf(err));
        })
        .finally(() => {
          if (live) setLoading(false);
        });
    }, 280);
    return () => {
      live = false;
      window.clearTimeout(id);
    };
  }, [api, query]);

  const chosen = new Set(selected.map((u) => u.id));
  const atCeiling = selected.length >= BROADCAST_MAX_NAMED_USERS;
  const { unaddressable } = splitAddressable(selected);

  function toggle(user: AdminUserRow): void {
    if (chosen.has(user.id)) {
      onChange(selected.filter((u) => u.id !== user.id));
      return;
    }
    if (atCeiling) return;
    onChange([...selected, user]);
  }

  return (
    <>
      <SearchInput value={query} onChange={setQuery} placeholder="Search by name or tier" />

      {selected.length > 0 && (
        <div className="picked">
          {selected.map((user) => (
            <span className="picked__chip" key={user.id}>
              {recipientLabel(user)}
              <button
                type="button"
                aria-label={`Remove ${recipientLabel(user)}`}
                onClick={() => toggle(user)}
              >
                <IconX size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {unaddressable.length > 0 && (
        <div className="mailnote" role="status">
          {unaddressable.length === 1
            ? '1 of the customers you picked has no email address on file and will not be counted.'
            : `${String(unaddressable.length)} of the customers you picked have no email address on file and will not be counted.`}
        </div>
      )}

      {atCeiling && (
        <div className="mailnote" role="status">
          That is the most customers you can name by hand ({BROADCAST_MAX_NAMED_USERS}). Use a
          group for anything larger.
        </div>
      )}

      {error !== null ? (
        <ErrorBox message={error} />
      ) : loading && rows.length === 0 ? (
        <LoadingState label="Searching…" />
      ) : rows.length === 0 ? (
        <div className="faint" style={{ fontSize: 13, padding: '12px 0' }}>
          No customers match that search.
        </div>
      ) : (
        <ul className="picklist">
          {rows.slice(0, 25).map((user) => {
            const on = chosen.has(user.id);
            const noEmail = user.email === null || user.email === '';
            return (
              <li key={user.id}>
                <button
                  type="button"
                  className={`picklist__row${on ? ' picklist__row--on' : ''}`}
                  disabled={noEmail || (!on && atCeiling)}
                  onClick={() => toggle(user)}
                >
                  <span className="picklist__main">
                    <span className="picklist__name">{recipientLabel(user)}</span>
                    <span className="faint mono" style={{ fontSize: 12 }}>
                      {noEmail ? 'no email address on file' : user.email}
                    </span>
                  </span>
                  <Badge tone={user.status === 'active' ? 'green' : 'red'} dot>
                    {user.status}
                  </Badge>
                  <span className="picklist__pick">{on ? 'Picked' : noEmail ? '-' : 'Add'}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <p className="faint" style={{ fontSize: 12 }}>
        <Link to="/users" className="mailnote__link">
          The Users page
        </Link>{' '}
        is the place to look somebody up properly. This search is only for building the list.
      </p>
    </>
  );
}
